/**
 * Der Bericht als ein Blatt, dann zum Aufklappen (seit 09.10.2026, Variante A
 * aus dem Canvas „99takes Report-Template“, von Robert gewählt).
 *
 * Drei Ebenen: oben das, was eine Entscheiderin auf einem Bildschirm braucht,
 * darunter die Befunde je Abschnitt der Seite, ganz unten alles Weitere zum
 * Aufklappen. Oben standen bis zum 12.10.2026 Urteil, Umfang und drei
 * Kennzahl-Karten; seitdem der Kopf mit Zeitleiste (bericht-vorgehen.ts), und
 * hier stehen dafür die Wortstufen.
 *
 * Hier steht nur Rechnung, keine Darstellung: alles aus der eingefrorenen
 * Nutzlast (`report.ts`), auch für ältere Berichte. Fehlt ein Feld, liefert die
 * Funktion null oder eine leere Liste, und die Seite lässt den Teil weg.
 * Keine Zahl wird geschätzt.
 */

import { anzahlUrteile, personaNoten } from './bericht-noten';
import { leseKernRand, leseKernRandWeiter } from './kern-rand';
import { leseAbschnitte, leseSchritt, type AbschnittBefund, type SchrittAnteile, type SchrittBilanz, type Stimme } from './befunde';
import { designEintraege, KRITERIEN } from './bericht-ablauf';
import { locale, sprache, t } from './sprache';

/**
 * Namen der Kriterien des Design-Checks. Getter statt Werte: sie lesen die
 * Sprache beim Zugriff, nicht beim Laden des Moduls.
 */
export const KRITERIUM: Record<string, string> = {
  get erster_eindruck() { return t('Erster Eindruck', 'First impression'); },
  get passt_zu_mir() { return t('Wirkt für Leute wie mich gemacht', 'Looks made for people like me'); },
  get glaubwuerdig() { return t('Seriös', 'Credible'); },
  get orientierung() { return t('Orientierung', 'Orientation'); },
  get handy() { return t('Am Handy', 'On mobile'); },
};

const zahl1 = (n: number) => new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
const istZahl = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const ganz = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Die Fassung, auf die sich Kennzahlen und Befunde beziehen: die letzte. */
export function standDer(data: any): any | null {
  const v = Array.isArray(data?.variants) ? data.variants : [];
  return v.length ? v[v.length - 1] : null;
}

// ─── Ebene 1: Urteil und Umfang ──────────────────────────────────────────────

/**
 * Die Überschrift. Vorrang hat das Urteil, das der Autor dafür schreibt
 * (`narrative.urteil`, seit 09.10.2026), dann die erste Kernaussage. Ohne
 * beides null: dann rechnet die Seite den Satz aus dem Ergebnis („… gewinnt
 * 5 : 1“), wie ältere Berichte; erst ohne Ergebnis kommt `ersterSatz`.
 */
export function urteilDesBerichts(data: any): string | null {
  const n = data?.narrative ?? {};
  const urteil = text(n.urteil);
  if (urteil) return urteil;
  const kp = Array.isArray(n.kernpunkte) ? text(n.kernpunkte[0]?.titel) : '';
  return kp || null;
}

/** Der erste Satz der Zusammenfassung, wenn er als Überschrift kurz genug ist. */
export function ersterSatz(data: any): string | null {
  const erster = text(data?.narrative?.summary).match(/^.+?[.!?](?=\s|$)/)?.[0] ?? '';
  return erster && erster.length <= 140 ? erster : null;
}

/** Kern und Rand der Personas im Bericht, gezählt am Etikett. */
export function kernRandZahl(data: any): { kern: number; rand: number } | null {
  const ps = Array.isArray(data?.set?.personas) ? data.set.personas : [];
  const rand = ps.filter((p: any) => p?.rolle === 'rand').length;
  return rand > 0 && rand < ps.length ? { kern: ps.length - rand, rand } : null;
}

/** Die Zahl der Personas in der Zeile „Grundlage“: aus `umfang`, sonst aus dem Bericht. */
function personasImUmfang(data: any): number {
  const u = data?.umfang;
  const personasImBericht = Array.isArray(data?.set?.personas) ? data.set.personas.length : 0;
  return ganz(u?.runden) && ganz(u?.personas) ? ganz(u.personas) : personasImBericht;
}

/**
 * Kern und Rand in der Zeile „Grundlage“, nur wenn sie zur Zahl der Personas
 * passen. Dann steht darunter auch, was die beiden Wörter heißen.
 */
export function kernRandImUmfang(data: any): { kern: number; rand: number } | null {
  const kr = kernRandZahl(data);
  return kr && kr.kern + kr.rand === personasImUmfang(data) ? kr : null;
}

/**
 * „8 Testrunden · 234 Urteile · 18 Personas (13 aus der Kernzielgruppe, 5 aus
 * der erweiterten Zielgruppe) · 3 Design-Checks“. Aus `umfang` (die ganze Studie, seit
 * 09.10.2026), sonst aus dem, was der Bericht zeigt. Kern und Rand nur, wenn
 * sie zur Zahl der Personas passen. Bis 12.10.2026 „(13 Kern, 5 Rand)“; das
 * verstand ein Leser nicht.
 */
export function umfangKurz(data: any): string {
  const u = data?.umfang;
  const varianten = Array.isArray(data?.variants) ? data.variants : [];
  const design = Array.isArray(data?.design) ? data.design.length : 0;
  const runden = ganz(u?.runden);
  const urteile = runden ? ganz(u.urteile)
    : varianten.reduce((s: number, v: any) => s + (anzahlUrteile(v) || ganz(v?.wins?.total)), 0);
  const personas = personasImUmfang(data);
  const checks = runden ? ganz(u.design_checks) : design;
  const kr = kernRandImUmfang(data);
  if (sprache() === 'en') {
    return [
      runden ? `${runden} ${runden === 1 ? 'test round' : 'test rounds'}`
        : varianten.length ? `${varianten.length} ${varianten.length === 1 ? 'version' : 'versions'}` : '',
      urteile ? `${urteile} ${urteile === 1 ? 'verdict' : 'verdicts'}` : '',
      personas ? `${personas} ${personas === 1 ? 'persona' : 'personas'}${kr ? ` (${kr.kern} from the core audience, ${kr.rand} from the extended audience)` : ''}` : '',
      checks ? `${checks} ${checks === 1 ? 'design check' : 'design checks'}` : '',
    ].filter(Boolean).join(' · ');
  }
  const teile = [
    runden ? `${runden} ${runden === 1 ? 'Testrunde' : 'Testrunden'}`
      : varianten.length ? `${varianten.length} ${varianten.length === 1 ? 'Fassung' : 'Fassungen'}` : '',
    urteile ? `${urteile} ${urteile === 1 ? 'Urteil' : 'Urteile'}` : '',
    personas ? `${personas} Personas${kr ? ` (${kr.kern} aus der Kernzielgruppe, ${kr.rand} aus der erweiterten Zielgruppe)` : ''}` : '',
    checks ? `${checks} ${checks === 1 ? 'Design-Check' : 'Design-Checks'}` : '',
  ];
  return teile.filter(Boolean).join(' · ');
}

// ─── Wortstufen (seit 12.10.2026) ────────────────────────────────────────────

/**
 * Wie eine Fassung gegen die Vergleichsfassung abschneidet, in fünf Wörtern
 * statt einer Note. ANLASS: ein Leser des Magnolia-Berichts verstand oben „5,9
 * zu 6,9“ ohne Herleitung nicht. Die Zahlen stehen weiter im Aufklapper „Wie
 * die Stufen entstehen“ und in den Akkordeons; oben steht das Wort. Bis zum
 * 12.10.2026 standen hier die drei Kennzahl-Karten (Text im Vergleich,
 * Nächster Schritt, Wie die Seite wirkt).
 *
 * Immer relativ zur Vergleichsfassung, nie absolut. Index 0 bis 4, für die
 * Skala mit fünf Segmenten.
 */
export const STUFEN = ['klar schwächer', 'etwas schwächer', 'gleichauf', 'etwas besser', 'klar besser'] as const;
export const STUFEN_EN = ['clearly weaker', 'slightly weaker', 'level', 'slightly better', 'clearly better'] as const;
/** Das Wort einer Stufe in der Sprache der Anzeige. */
export const stufenWort = (stufe: StufeNr): string => t<readonly string[]>(STUFEN, STUFEN_EN)[stufe]!;
export type StufeNr = 0 | 1 | 2 | 3 | 4;

/** Unter diesem Abstand der Noten heißt es „gleichauf“. */
export const GLEICHAUF_UNTER = 0.3;
/** Ab diesem Abstand „klar“, darunter „etwas“. */
export const KLAR_AB = 1.0;

const rund1 = (n: number) => Math.round(n * 10) / 10;
const vorzeichen = (n: number): -1 | 0 | 1 => (n > 0 ? 1 : n < 0 ? -1 : 0);

/**
 * Die Regel, rein. `delta` ist Note der Fassung minus Note der Vergleichsfassung,
 * auf eine Stelle gerundet (sonst wäre 5,6 gegen 6,6 nur 0,99999 und „etwas“).
 * Gleichauf, wenn sich die Wertung teilt (`geteilt`) oder der Abstand unter 0,3
 * liegt; sonst „etwas“ unter 1,0 und „klar“ ab 1,0. Die Richtung kommt aus dem
 * Abstand. Widersprechen sich Abstand und Mehrheit (`richtung`, aus den
 * Siegen), entscheidet die Mehrheit die Richtung und der Abstand die Stärke:
 * wer „etwas besser“ liest, soll darunter nicht finden, dass die meisten die
 * Vergleichsfassung vorziehen. Ohne Abstand, aber mit Mehrheit: „etwas“.
 * Ohne beides null.
 */
export function wortstufe(delta: number | null | undefined, opt: { geteilt?: boolean; richtung?: number } = {}): StufeNr | null {
  const d = istZahl(delta) ? rund1(delta) : null;
  const mehrheit = vorzeichen(opt.richtung ?? 0);
  if (d == null && !mehrheit && !opt.geteilt) return null;
  if (opt.geteilt) return 2;
  if (d != null && Math.abs(d) < GLEICHAUF_UNTER) return 2;
  const richtung = mehrheit || vorzeichen(d ?? 0);
  if (!richtung) return 2;
  const klar = d != null && Math.abs(d) >= KLAR_AB;
  return richtung > 0 ? (klar ? 4 : 3) : (klar ? 0 : 1);
}

export interface FassungsStufe {
  stufe: StufeNr;
  wort: string;
  /** Noten im Kern, wenn es Kern und Rand gibt, sonst über alle. */
  fassung: number | null;
  vergleich: number | null;
  delta: number | null;
  dafuer: number;
  von: number;
  basis: 'kern' | 'alle';
  /** Zählt die Mehrheit Urteile statt Personas (Matrix: eine Persona urteilt mehrmals)? */
  urteile: boolean;
  /** „4 von 8 aus der Kernzielgruppe ziehen sie vor“ */
  satz: string;
}

/**
 * Die Wortstufe einer Fassung aus ihrem Vergleich. Gerechnet im Kern, wenn es
 * Kern und Rand gibt (`kern_rand.kern`), sonst über alle. Feedback-Runs haben
 * keinen Vergleich: null (dafür `weiterSatz`).
 */
export function textStufe(v: any): FassungsStufe | null {
  if (!v || v.mode === 'feedback') return null;
  const kr = leseKernRand(v);
  const c = kr ? kr.kern.scores.candidate : (istZahl(v.scores?.candidate) ? v.scores.candidate : null);
  const b = kr ? kr.kern.scores.baseline : (istZahl(v.scores?.baseline) ? v.scores.baseline : null);
  const dafuer = kr ? kr.kern.wins.candidate : ganz(v.wins?.candidate);
  const von = kr ? kr.kern.n : ganz(v.wins?.total);
  const dagegen = kr ? kr.kern.wins.baseline : Math.max(0, von - dafuer);
  const delta = c != null && b != null ? rund1(c - b) : null;
  const stufe = wortstufe(delta, { geteilt: von > 0 && dafuer === dagegen, richtung: von > 0 ? dafuer - dagegen : 0 });
  if (stufe == null) return null;
  const urteile = anzahlUrteile(v) > personaNoten(v).length;
  const eins = dafuer === 1;
  const satz = sprache() === 'en'
    ? urteile
      ? `${dafuer} of ${von} ${von === 1 ? 'verdict' : 'verdicts'}${kr ? ' from the core audience' : ''} ${eins ? 'favors' : 'favor'} it`
      : `${dafuer} of ${von} ${kr ? 'from the core audience' : von === 1 ? 'persona' : 'personas'} ${eins ? 'prefers' : 'prefer'} it`
    : urteile
      ? `${dafuer} von ${von} Urteilen${kr ? ' der Kernzielgruppe' : ''} ${eins ? 'spricht' : 'sprechen'} für sie`
      : `${dafuer} von ${von} ${kr ? 'aus der Kernzielgruppe' : 'Personas'} ${eins ? 'zieht' : 'ziehen'} sie vor`;
  return { stufe, wort: stufenWort(stufe), fassung: c, vergleich: b, delta, dafuer, von, basis: kr ? 'kern' : 'alle', urteile, satz };
}

/** Ein Run ohne Vergleich: „5 von 8 aus der Kernzielgruppe würden weitergehen“. Ohne Zahlen leer. */
export function weiterSatz(v: any): string {
  if (!v || v.mode !== 'feedback') return '';
  const kr = leseKernRandWeiter(v);
  if (kr) {
    return t(`${kr.kern.weiter} von ${kr.kern.n} aus der Kernzielgruppe würden weitergehen`,
      `${kr.kern.weiter} of ${kr.kern.n} from the core audience would continue`);
  }
  const gesamt = ganz(v.wins?.total);
  if (!gesamt) return '';
  return t(`${ganz(v.wins?.candidate)} von ${gesamt} Personas würden weitergehen`,
    `${ganz(v.wins?.candidate)} of ${gesamt} ${gesamt === 1 ? 'persona' : 'personas'} would continue`);
}

const sicht = (b: SchrittBilanz | null): SchrittAnteile | null => (b ? (b.basis === 'kern' && b.kern ? b.kern : b) : null);

/**
 * Wer den nächsten Schritt gehen würde (Anteil „ja“, im Kern, wenn es ihn
 * gibt), für die Fassung und die Vergleichsfassung. Stand bis zum 12.10.2026
 * als Karte „Nächster Schritt“ oben, jetzt in der Tabelle des Aufklappers.
 * Runs vor dem 09.10.2026 haben das Feld nicht: null.
 */
export function schrittJa(v: any): { fassung: number | null; vergleich: number | null } | null {
  if (!v) return null;
  if (v.mode === 'feedback') {
    const s = sicht(leseSchritt(v.naechster_schritt));
    return s ? { fassung: s.anteil.ja, vergleich: null } : null;
  }
  const sn = sicht(leseSchritt(v.naechster_schritt?.candidate));
  const sa = sicht(leseSchritt(v.naechster_schritt?.baseline));
  return sn || sa ? { fassung: sn ? sn.anteil.ja : null, vergleich: sa ? sa.anteil.ja : null } : null;
}

export interface GeraetStufe {
  /** Zugleich das Etikett: auf Englisch „Mobile“ statt „Handy“. „Desktop“ bleibt in beiden Sprachen. */
  geraet: 'Desktop' | 'Handy' | 'Mobile';
  /** Note der heutigen Seite und der Seite mit der neuen Fassung (Skizze), auf eine Stelle. */
  heute: number;
  neu: number;
  delta: number;
  stufe: StufeNr;
  wort: string;
}

const AM_DESKTOP = ['erster_eindruck', 'passt_zu_mir', 'glaubwuerdig', 'orientierung'] as const;

/**
 * Die Gestaltung je Gerät als Wortstufe: heute gegen die Seite mit der neuen
 * Fassung (Skizze). Desktop ist das Mittel der vier Kriterien ohne „Am Handy“,
 * Handy die Note „Am Handy“; nur Kriterien, die beide Checks tragen. Dieselbe
 * Regel wie beim Text, ohne Mehrheit: zwei getrennte Checks, kein Vergleich je
 * Persona. Magnolia: Desktop 7,0 heute, 6,3 mit der Skizze, also „etwas
 * schwächer“; Handy 5,9 und 4,9, also „klar schwächer“.
 */
export function designStufen(heute: any, skizze: any): GeraetStufe[] {
  const out: GeraetStufe[] = [];
  const a = heute?.noten ?? {};
  const b = skizze?.noten ?? {};
  const beide = AM_DESKTOP.filter((k) => istZahl(a[k]) && istZahl(b[k]));
  const mittel = (n: any) => rund1(beide.reduce((s, k) => s + n[k], 0) / beide.length);
  const zeile = (geraet: GeraetStufe['geraet'], h: number, n: number) => {
    const delta = rund1(n - h);
    const stufe = wortstufe(delta)!;
    out.push({ geraet, heute: h, neu: n, delta, stufe, wort: stufenWort(stufe) });
  };
  if (beide.length) zeile('Desktop', mittel(a), mittel(b));
  if (istZahl(a.handy) && istZahl(b.handy)) zeile(t('Handy', 'Mobile'), rund1(a.handy), rund1(b.handy));
  return out;
}

export interface DesignKarte { note: number; was: string; satz: string }

/**
 * Wie die Seite wirkt: der erste Eindruck der Live-Seite (sonst das Mittel der
 * Noten) und ein Satz zur schwächsten Stelle. Ohne Design-Check null.
 */
export function designKarte(design: unknown): DesignKarte | null {
  const eintraege = designEintraege(Array.isArray(design) ? design : []);
  const erster = eintraege[0];
  if (!erster) return null;
  const d = erster.art === 'paar' ? erster.heute : erster.d;
  const noten = d?.noten ?? {};
  const werte = KRITERIEN.map((k) => noten[k]).filter(istZahl);
  let note: number | null = istZahl(noten.erster_eindruck) ? noten.erster_eindruck : null;
  let was = t('von 10, erster Eindruck', 'out of 10, first impression');
  if (note == null && werte.length) {
    note = werte.reduce((s, v) => s + v, 0) / werte.length;
    was = t('von 10, Mittel der Noten', 'out of 10, average of the scores');
  }
  if (note == null) return null;
  const saetze: string[] = [];
  const k = text(d?.schwaechste);
  if (k && istZahl(noten[k]) && k !== 'erster_eindruck') saetze.push(t(`Schwächste Stelle: ${KRITERIUM[k] ?? k} (${zahl1(noten[k])}).`, `Weakest point: ${KRITERIUM[k] ?? k} (${zahl1(noten[k])}).`));
  if (erster.art === 'paar' && istZahl(erster.skizze?.noten?.erster_eindruck)) {
    saetze.push(t(`Mit der neuen Fassung als Skizze: ${zahl1(erster.skizze.noten.erster_eindruck)}.`,
      `With the new version as a sketch: ${zahl1(erster.skizze.noten.erster_eindruck)}.`));
  }
  const grad3 = (Array.isArray(d?.handwerk?.befunde) ? d.handwerk.befunde : []).filter((b: any) => Number(b?.schwere) >= 3).length;
  if (grad3) {
    saetze.push(t(
      `Der Fachprüfer findet ${grad3 === 1 ? 'einen Befund' : `${grad3} Befunde`}, ${grad3 === 1 ? 'der' : 'die'} spürbar ${grad3 === 1 ? 'kostet' : 'kosten'}.`,
      `The expert reviewer finds ${grad3 === 1 ? 'one finding that costs' : `${grad3} findings that cost`} noticeably.`,
    ));
  }
  return { note, was, satz: saetze.slice(0, 2).join(' ') };
}

// ─── Ebene 1: die geprüfte Seite ─────────────────────────────────────────────

export interface GepruefteSeite {
  /** Bildadressen (neu signiert bei jedem Abruf), nur http(s). Mindestens eine ist da. */
  desktop: string | null;
  mobil: string | null;
  /** Die geprüfte Adresse für „Seite ansehen“, nur wenn sie wirklich das Gezeigte ist. */
  url: string | null;
  /** Woher die Bilder kommen; beim ersten Bildschirm mit dem Namen der Variante. */
  herkunft: { art: 'design' } | { art: 'erster_bildschirm'; variante: string };
}

const bildLink = (v: unknown): string | null => {
  const s = text(v);
  if (!s || /[\u0000-\u001f\u007f]/.test(s) || !/^https?:\/\//i.test(s)) return null;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
};

/** `…#entwurf-ab12` hängt 99takes selbst an (entwurf.ts); das gehört nicht in den Link. */
const ohneEntwurfsAnker = (url: string) => url.replace(/#entwurf-[^#]*$/, '');

export function bilderVon(links: any): { desktop: string | null; mobil: string | null } | null {
  const desktop = bildLink(links?.desktop);
  const mobil = bildLink(links?.mobil);
  return desktop || mobil ? { desktop, mobil } : null;
}

/**
 * Was oben im Bericht als „Die geprüfte Seite“ steht (seit 09.10.2026, Robert
 * am Scopevisio-Bericht: die Bilder standen nur im zugeklappten Design-Check,
 * niemand sah, was geprüft wurde). Vorrang hat der Design-Check, den auch die
 * Karte „Wie die Seite wirkt“ nimmt (der jüngste, bei Heute gegen Skizze die
 * heutige Seite); hat er keine Bilder, der nächste mit Bildern. Ohne
 * Design-Check die vorn liegende Variante des jüngsten Vergleichs der ersten
 * Bildschirme. Ohne Bilder null, dann fehlt der Block (alte Berichte).
 *
 * Der Link „Seite ansehen“ nur bei einer echten Seite: bei einem Entwurf (HTML
 * oder Bilder) oder einer Skizze ist die Adresse nicht das, was die Personas
 * gesehen haben.
 */
export function gepruefteSeite(data: any): GepruefteSeite | null {
  const eintraege = designEintraege(Array.isArray(data?.design) ? data.design : []);
  const checks = eintraege.flatMap((e) => (e.art === 'paar' ? [e.heute, e.skizze] : [e.d]));
  for (const d of checks) {
    const b = bilderVon(d?.bild_links);
    if (!b) continue;
    const echt = !d?.quelle && !d?.skizze;
    const link = echt ? bildLink(ohneEntwurfsAnker(text(d?.url))) : null;
    return { ...b, url: link, herkunft: { art: 'design' } };
  }

  const tests = Array.isArray(data?.erster_bildschirm) ? data.erster_bildschirm : [];
  for (const test of tests) {
    const varianten: any[] = (Array.isArray(test?.varianten) ? test.varianten : []).filter((v: any) => bilderVon(v?.bild_links));
    if (!varianten.length) continue;
    const fuehrt = test?.urteil?.fuehrt;
    const vorn = varianten.find((v) => fuehrt != null && v?.nummer === fuehrt)
      ?? [...varianten].sort((a, b) => (Number(b?.anteil) || 0) - (Number(a?.anteil) || 0))[0];
    const name = text(vorn?.name);
    return {
      ...bilderVon(vorn.bild_links)!,
      url: bildLink(ohneEntwurfsAnker(text(vorn?.url))),
      herkunft: {
        art: 'erster_bildschirm',
        variante: t(`Variante ${vorn?.nummer ?? ''}${name ? ` „${name}“` : ''}`, `Variant ${vorn?.nummer ?? ''}${name ? ` “${name}”` : ''}`).trim(),
      },
    };
  }
  return null;
}

// ─── Ebene 1: was ihr jetzt tun solltet ──────────────────────────────────────

/** Der erste Satz, höchstens rund 140 Zeichen, am Wort gekürzt. */
export function ersteZeile(t: unknown, max = 140): string {
  const s = text(t).replace(/\s+/g, ' ');
  if (!s) return '';
  const satz = s.match(/^.+?[.!?](?=\s|$)/)?.[0] ?? s;
  if (satz.length <= max) return satz;
  const kurz = satz.slice(0, max);
  return `${kurz.slice(0, Math.max(kurz.lastIndexOf(' '), 40)).replace(/[,;:\s]+$/, '')} …`;
}

export interface Tun { nr: number; titel: string; zeile: string }

/** Die ersten drei Empfehlungen des Autors, je Titel und eine Zeile. */
export function dreiZuTun(empfehlungen: unknown): Tun[] {
  return (Array.isArray(empfehlungen) ? empfehlungen : [])
    .map((e: any, i: number) => ({ nr: i + 1, titel: text(e?.titel), zeile: ersteZeile(e?.text) || ersteZeile(e?.vorschlag) }))
    .filter((t) => t.titel)
    .slice(0, 3);
}

// ─── Ebene 2: Befunde je Abschnitt ───────────────────────────────────────────

const norm = (s: unknown) => String(s ?? '').toLowerCase().normalize('NFC')
  .replace(/[„“”"'‚‘’«»]/g, '').replace(/\s+/g, ' ').trim();

const FUELL = new Set(['nicht', 'nichts', 'wenn', 'oder', 'eine', 'einen', 'einer', 'statt', 'auch', 'kein', 'keine', 'nach',
  'über', 'unter', 'dass', 'wird', 'sind', 'ihre', 'ihr', 'ihren', 'ihrem', 'unsere', 'unser', 'eure', 'euer', 'abschnitt', 'seite', 'teil']);
const staemme = (s: unknown) => new Set(norm(s).split(/[^a-zäöüß0-9]+/).filter((w) => w.length >= 4 && !FUELL.has(w)).map((w) => w.slice(0, 5)));

/** So nennen Autoren den Einstieg, wenn sie das Wort der Abschnittsliste nicht nehmen. */
const EINSTIEG = ['einstieg', 'hero', 'headline', 'h1', 'subline', 'erster bildschirm', 'ersten bildschirm', 'oberer teil', 'kopf der seite'];

/**
 * Wie gut eine Empfehlung zu einem Abschnitt passt, 0 heißt gar nicht.
 * 3: der Autor hat den Abschnitt genannt (`abschnitt`); 2: der Name steht im
 * Titel; 1,8: Einstieg über ein gängiges Wort (Hero, Headline); sonst die
 * Überdeckung der Wortstämme des Abschnittsnamens im Titel, ab der Hälfte.
 */
export function passung(e: any, abschnitt: string): number {
  const a = norm(abschnitt);
  if (!a) return 0;
  const genannt = norm(e?.abschnitt);
  if (genannt) {
    if (genannt === a) return 3;
    if (genannt.length >= 4 && (genannt.includes(a) || a.includes(genannt))) return 2.5;
  }
  const titel = norm(e?.titel);
  if (a.length >= 3 && new RegExp(`(^|[^a-zäöüß0-9])${a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-zäöüß0-9])`).test(titel)) return 2;
  if (a === 'einstieg' && EINSTIEG.some((w) => new RegExp(`(^|[^a-zäöüß0-9])${w}($|[^a-zäöüß0-9])`).test(titel))) return 1.8;
  const sa = staemme(abschnitt);
  if (!sa.size) return 0;
  const st = staemme(titel);
  const gemeinsam = [...sa].filter((w) => st.has(w)).length;
  const anteil = gemeinsam / sa.size;
  return gemeinsam > 0 && anteil >= 0.5 ? 1 + anteil / 2 : 0;
}

export type Status = 'ok' | 'fix';
export type Art = 'wirkt' | 'bremst' | 'fehlt';

export interface Zahl { n: number; von: number }

export interface AbschnittsZeile {
  nr: number;
  titel: string;
  status: Status;
  /** Was auf dem Etikett steht: „Trägt“, der Titel der Empfehlung, oder was überwiegt. */
  etikett: string;
  /** Der Text der passenden Empfehlung, sonst leer. */
  befund: string;
  /** Typische Aussagen im Wortlaut, die erste ist die, die zum Status passt. */
  zitate: Array<Stimme & { art: Art }>;
  /** Vorschlag und Nummer der passenden Empfehlungen (1-basiert). */
  empfehlungen: Array<{ nr: number; vorschlag: string }>;
  zahlen: Record<Art, Zahl>;
  /** Zählen die Zahlen nur den Kern? */
  kern: boolean;
}

export interface Befunde {
  zeilen: AbschnittsZeile[];
  /** Empfehlungen (1-basiert), die keinem Abschnitt zugeordnet sind. */
  ohneAbschnitt: number[];
}

/** „Abschnitt 2, KI-Teil: Kürzen und nach hinten“ → „Kürzen und nach hinten“. Höchstens 44 Zeichen. */
export function etikettAus(titel: string, abschnitt: string): string {
  let t = text(titel);
  const doppel = t.indexOf(':');
  if (doppel > 0 && doppel < t.length - 3) {
    const vorn = norm(t.slice(0, doppel));
    if (/abschnitt|einstieg|hero/.test(vorn) || vorn.includes(norm(abschnitt))) t = t.slice(doppel + 1).trim();
  }
  t = t.charAt(0).toUpperCase() + t.slice(1);
  if (t.length <= 44) return t;
  const kurz = t.slice(0, 44);
  return `${kurz.slice(0, Math.max(kurz.lastIndexOf(' '), 20)).replace(/[,;:\s]+$/, '')} …`;
}

/**
 * Je Abschnitt der neuen Fassung (in der Reihenfolge der Seite) eine Zeile:
 * Status, Aussage, passende Empfehlung, wirkt/bremst/fehlt. Gezählt im Kern,
 * wenn es Kern und Rand gibt. Jede Empfehlung gehört höchstens einem
 * Abschnitt, dem mit der besten Passung; Gleichstand: der erste auf der Seite.
 * Ohne Abschnitte (Runs von vor dem 09.10.2026) leer.
 */
export function befundeJeAbschnitt(stand: any, empfehlungen: unknown): Befunde {
  const roh = stand?.mode === 'feedback' ? stand?.abschnitte : stand?.abschnitte?.candidate;
  const abschnitte: AbschnittBefund[] = leseAbschnitte(roh);
  const liste = Array.isArray(empfehlungen) ? empfehlungen : [];
  if (!abschnitte.length) return { zeilen: [], ohneAbschnitt: [] };

  const zuordnung = new Map<number, number[]>();
  const ohneAbschnitt: number[] = [];
  liste.forEach((e: any, i: number) => {
    let best = -1;
    let wert = 0;
    abschnitte.forEach((a, j) => {
      const p = passung(e, a.abschnitt);
      if (p > wert) { wert = p; best = j; }
    });
    if (best >= 0 && wert >= 1) zuordnung.set(best, [...(zuordnung.get(best) ?? []), i]);
    else if (text(e?.titel)) ohneAbschnitt.push(i + 1);
  });

  const zeilen = abschnitte.map((a, j): AbschnittsZeile => {
    const kern = a.kern_von != null && a.kern_von > 0;
    const von = kern ? a.kern_von! : a.von;
    const z = (art: Art): Zahl => ({ n: kern ? (a[art].kern ?? 0) : a[art].n, von });
    const zahlen = { wirkt: z('wirkt'), bremst: z('bremst'), fehlt: z('fehlt') };
    const eigene = (zuordnung.get(j) ?? []).map((i) => ({ i, e: liste[i] as any }));
    const ok = !eigene.length && zahlen.wirkt.n > zahlen.bremst.n + zahlen.fehlt.n;
    const problem: Art = zahlen.fehlt.n > zahlen.bremst.n ? 'fehlt' : 'bremst';
    const etikett = ok ? t('Trägt', 'Carries')
      : eigene.length ? etikettAus(text(eigene[0]!.e?.titel), a.abschnitt)
        : problem === 'fehlt' ? t('Es fehlt etwas', 'Something is missing') : t('Bremst noch', 'Still holds back');
    const reihenfolge: Art[] = ok ? ['wirkt', 'bremst', 'fehlt'] : [problem, problem === 'fehlt' ? 'bremst' : 'fehlt', 'wirkt'];
    const zitate: Array<Stimme & { art: Art }> = [];
    for (const art of reihenfolge) {
      const s = a[art].stimmen[0];
      if (s && !zitate.some((x) => x.text === s.text)) zitate.push({ ...s, art });
    }
    // Mit Empfehlung trägt ihr Text den Befund, dann reicht eine Aussage;
    // ohne stehen zwei, die überwiegende zuerst.
    const befund = eigene.length ? ersteZeile(eigene[0]!.e?.text, 260) : '';
    return {
      nr: j + 1,
      titel: a.abschnitt,
      status: ok ? 'ok' : 'fix',
      etikett,
      befund,
      zitate: zitate.slice(0, befund ? 1 : 2),
      empfehlungen: eigene.map(({ i, e }) => ({ nr: i + 1, vorschlag: text(e?.vorschlag) || ersteZeile(e?.text) })),
      zahlen,
      kern,
    };
  });
  return { zeilen, ohneAbschnitt };
}

/** Auf welchen Nenner die Zahlen der Abschnitte sich beziehen, für die Kopfzeile. */
export function nennerSatz(b: Befunde): string {
  const z = b.zeilen[0];
  if (!z) return '';
  return t(`Zahlen: ${z.kern ? 'Kern-Personas' : 'Personas'} von ${z.zahlen.wirkt.von}, die es nennen`,
    `Numbers: ${z.kern ? 'core personas' : 'personas'} out of ${z.zahlen.wirkt.von} who mention it`);
}
